import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Grid, makeStyles, Box, debounce } from '@material-ui/core';
import {
	Loader,
	ExtendedTheme,
	Link,
	ArrowsChevronSmallLeft,
	ToolbarButton,
	Toolbar,
	ToolbarButtonGroup,
	Checkbox
} from 'neon-design-system';
import { SUBSCRIPTIONS } from '../../../gql/subscriptions';
import SectionHeader from '../../ui/SectionHeader';
import { ChannelSubscription, EmailNotificationSettings } from '../../../shared/types';
import { useMutation } from '@apollo/react-hooks';
import CardNoContentHeading from '../../ui/card/CardNoContentHeading';
import Card from '../../ui/card/Card';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import { baseUrls } from '../../../utils/baseUrls.utils';
import PageHeader from '../../ui/PageHeader';
import {
	GET_USER_EMAIL_NOTIFICATION_SETTINGS,
	SET_USER_EMAIL_NOTIFICATION_SETTINGS,
	SET_PREMIUM_CHANNELS_EMAIL_NOTIFICATION_SETTINGS,
	UserSubscriptionsResponse
} from '../../../gql/users';

enum EnableStatus {
	NoAction,
	Enable,
	Disable
}

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => ({
	arrowLeftIcon: {
		marginRight: theme.spacing(0.5)
	},
	link: {
		display: 'flex',
		fontWeight: 'bold',
		verticalAlign: 'middle',
		marginBottom: theme.spacing(2)
	}
}));

export type EmailNotifications = 'channelNotifications'

const ManageEmailNotificationsPage = () => {
	const classes = useStyles();

	const pageLoadComplete = useActivityLogger('EmailNotifications', 'Email notifications', undefined, true);

	const [changingSubscription, setChangingSubscription] = useState<EnableStatus>(EnableStatus.NoAction);

	const querySubscriptions = useQuery<UserSubscriptionsResponse>(SUBSCRIPTIONS, { fetchPolicy: 'network-only' });
	const { data, loading } = useQuery(GET_USER_EMAIL_NOTIFICATION_SETTINGS, { fetchPolicy: 'network-only' });
	const [setEmailNotifications] = useMutation(SET_USER_EMAIL_NOTIFICATION_SETTINGS);
	const [setPremiumChannelsEmailNotifications] = useMutation(SET_PREMIUM_CHANNELS_EMAIL_NOTIFICATION_SETTINGS, {
		refetchQueries: [{ query: SUBSCRIPTIONS }]
	});

	const [emailNotifications, setEmailNotification] = useState<EmailNotificationSettings | null>(null);

	const debounceSetEmailNotifications = debounce((data) => setEmailNotifications({
		variables: {
			item: {...data}
		}
	}), 500);

	const subscribedChannels = querySubscriptions?.data?.me?.channelSubscriptions
		.filter(cs => cs?.channel && cs?.subscribed)
		.sort((a, b) => a.channel.name.localeCompare(b.channel.name)) || [];

	useEffect(() => {
		setEmailNotification({
			channelNotifications: !!data?.me?.emailNotificationSettings?.channelNotifications
		});
	}, [data]);

	useEffect(() => {
		setChangingSubscription(EnableStatus.NoAction);
	}, [changingSubscription]);

	if (querySubscriptions.loading || loading) return <Loader />;

	pageLoadComplete();

	const haveNotSubscribedToAnythingYet = !subscribedChannels.length;

	const handleToolbarButtonsClick = (type: EmailNotifications, value: boolean) => {
		if (!emailNotifications) return
		if (emailNotifications[type] === value) return;

		const formattedEmailNotifications: EmailNotificationSettings = {
			...emailNotifications,
			[type]: value
		}

		setEmailNotification(formattedEmailNotifications);
		debounceSetEmailNotifications(formattedEmailNotifications);
	}

	const onPremiumChannelsEmailNotificationsChange = (channelId: string) => {
		setPremiumChannelsEmailNotifications({
			variables: { channelId }
		});
	}

	const generateToolbarButtons = (type: EmailNotifications) => (
		<Toolbar>
			<ToolbarButtonGroup>
				<ToolbarButton
					text="On"
					selected={!!emailNotifications?.[type]}
					onClick={() => handleToolbarButtonsClick(type,true)}
				/>
				<ToolbarButton
					text="Off"
					selected={!emailNotifications?.[type]}
					onClick={() => handleToolbarButtonsClick(type,false)}
				/>
			</ToolbarButtonGroup>
		</Toolbar>
	)

	const channelsDisabledCheckbox = !emailNotifications?.channelNotifications;

	return (
		<Grid container spacing={4}>
			<Grid item xs={12}>
				<Link to={baseUrls.subscriptions} className={classes.link}>
					<ArrowsChevronSmallLeft className={classes.arrowLeftIcon} />
					Back to your subscriptions
				</Link>
				<PageHeader
					title="Update your email notifications"
					subtitle="Manage your email notifications to make Insights work better for you."
					subtitleInk="muted"
				/>
			</Grid>
			{
				haveNotSubscribedToAnythingYet ? (
					<Grid xs={12} item>
						<Box pt={0.5}>
							<Card>
								<CardNoContentHeading>You haven't subscribed to anything yet.</CardNoContentHeading>
							</Card>
						</Box>
					</Grid>
				) : (
					<Grid item xs={12} md={6}>
						<Grid container spacing={4}>
							{
								subscribedChannels.length ? (
									<Grid item xs={12}>
										<SectionHeader
											id="channels-header"
											title="Emails from Channels"
											subtitle="Receive emails from Channels you subscribe to when new articles are published"
											headingToolbar={generateToolbarButtons('channelNotifications')}
										/>
										<Grid container spacing={1}>
											{subscribedChannels.map((cs: ChannelSubscription, key: number) => (
												<Grid item xs={12} key={`channel_${key}`}>
													<Checkbox
														id="premium-channels-checkbox"
														label={cs.channel.name}
														value="a"
														checked={cs.emailNotifications}
														onChange={() => onPremiumChannelsEmailNotificationsChange(cs.channel._id)}
														disabled={channelsDisabledCheckbox}
													/>
												</Grid>
											))}
										</Grid>
									</Grid>
								) : null
							}
						</Grid>
					</Grid>
				)
			}
		</Grid>
	);
};

export default ManageEmailNotificationsPage;
